import {type MutableRefObject, useCallback, useEffect, useRef} from 'react';
import { isLoadError } from '../utils/loadable';
import { LoadableActionResult } from './useLoadableAction';

export type PollingRefreshFn = (errorCounterRef: MutableRefObject<number>) => void;

/**
 * Utility hook for Polling
 *
 * @param refreshFn a function to be polled with. Need to accept a ref to increment the error counter as appropriate
 * @param frequency optional, ms between each poll
 * @param errorTolerance optional, number of errors before polling stops
 */
export const usePolling = (
  refreshFn: (errorCounterRef: MutableRefObject<number>) => void,
  frequency = 10000,
  errorTolerance = 3
) => {
  const errorCounterRef = useRef(0);
  const intervalRef = useRef<number>();

  const guardedRefresh = useCallback(() => {
    if (errorCounterRef.current > errorTolerance) {
      window.clearInterval(intervalRef.current);
      return;
    }
    refreshFn(errorCounterRef);
  }, [errorTolerance, refreshFn]);

  // using a ref because the interval should never have to be recreated
  // just because function ref changed
  const guardedRefreshRef = useRef(guardedRefresh);
  guardedRefreshRef.current = guardedRefresh;

  useEffect(() => {
    guardedRefreshRef.current();

    intervalRef.current = window.setInterval(() => {
      guardedRefreshRef.current();
    }, frequency);

    return () => {
      window.clearInterval(intervalRef.current);
    };
  }, [frequency]);
};

/**
 * Utility hook for Polling with a loadable
 *
 * @param loadableAction loadable to poll with
 * @param params params to pass to loadable's loader to poll with
 * @param frequency optional, ms between each poll
 * @param errorTolerance optional, number of errors before polling stops
 */
export const usePollingLoadable = <TData, TParams extends any[]>(
  loadableAction: LoadableActionResult<TData, TParams>,
  params: TParams,
  frequency = 300 * 1000,
  errorTolerance = 3
) => {
  const [loadable, doLoad] = loadableAction;

  // when params change, we don't need to recreate the effect
  // just need to make sure each call is with the latest params
  const paramsRef = useRef<TParams>(params);
  paramsRef.current = params;

  const refresh = useCallback(
    (errorCounterRef: MutableRefObject<number>) => {
      // this results in an off by one with retries
      // since we are going through when the counter first reach tolerance
      // but it should not matter if we are off by one in this case
      if (isLoadError(loadable)) {
        errorCounterRef.current += 1;
      }
      doLoad(...paramsRef.current);
    },
    [doLoad, loadable]
  );

  usePolling(refresh, frequency, errorTolerance);
};
